import { useState, useEffect, useRef } from 'react'
import { Avatar } from './Avatar'
import { publicName } from '../lib/displayName'

// ── FirstConnection ─────────────────────────────────────────────────────────
// The moment a friendless account becomes a social graph — the first person you
// follow. It used to be silent (the button just flipped to "Following"). Now the
// two of you slide in like two film frames splicing onto one strip, with the
// count of shows you both love and your shared top genre on a casting slate.
//
// Fires on window event `nextep:first-connection` with detail:
//   { person:{ display_name, username, avatar_url }, sharedCount:Number, topGenre?:String }
// `user` (the follower) is passed as a prop so we can show both faces.

export function FirstConnection({ user }) {
  const [data, setData]   = useState(null)  // { person, sharedCount, topGenre }
  const [phase, setPhase] = useState(0)      // 0=hidden 1=in 2=exit
  const timers = useRef([])
  const clear  = () => { timers.current.forEach(clearTimeout); timers.current = [] }
  const later  = (fn, ms) => { const t = setTimeout(fn, ms); timers.current.push(t) }

  useEffect(() => {
    if (!document.getElementById('first-connection-styles')) {
      const el = document.createElement('style')
      el.id = 'first-connection-styles'
      el.textContent = STYLES
      document.head.appendChild(el)
    }
    const handler = (e) => {
      const d = e.detail || {}
      if (!d.person) return
      clear()
      setData({ person: d.person, sharedCount: Number(d.sharedCount) || 0, topGenre: d.topGenre || null })
      setPhase(1)
      later(() => setPhase(2), 3200)
      later(() => { setPhase(0); setData(null) }, 3650)
    }
    window.addEventListener('nextep:first-connection', handler)
    return () => { window.removeEventListener('nextep:first-connection', handler); clear() }
  }, [])

  if (!data) return null
  const { person, sharedCount, topGenre } = data
  // Don't pull a name from OAuth user_metadata (would surface a full legal name if
  // Google sign-in is ever added). This is the user's OWN celebration, so a handle is fine.
  const meName    = user?.email?.split('@')[0] || 'You'
  const themName  = publicName(person)

  return (
    <div onClick={() => { setPhase(2); later(() => { setPhase(0); setData(null) }, 350) }} style={{
      position: 'fixed', inset: 0, zIndex: 'var(--z-celebration)',
      background: 'radial-gradient(ellipse at 50% 45%, rgba(10,8,20,0.92) 0%, rgba(var(--bg-rgb),0.96) 70%)',
      backdropFilter: 'blur(6px)', WebkitBackdropFilter: 'blur(6px)',
      display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
      fontFamily: 'var(--font)', userSelect: 'none',
      opacity: phase === 1 ? 1 : 0, transition: 'opacity 0.35s ease',
    }}>
      <div style={{
        color: 'var(--amber)', fontWeight: 700, fontSize: 11.5, letterSpacing: '0.24em',
        textTransform: 'uppercase', marginBottom: 26,
        '--d': '0.5s',
      }} className={phase === 1 ? 'nxa-fc-fadeup' : undefined}>
        Now co-starring
      </div>

      {/* Two frames splicing together */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 22 }}>
        <div style={{
          padding: 4, borderRadius: '50%', border: '2px solid rgba(var(--ink-rgb),0.15)',
        }} className={phase === 1 ? 'nxa-fc-frame-l' : undefined}>
          <Avatar name={meName} avatarUrl={user?.user_metadata?.avatar_url} size={72} />
        </div>

        {/* Splice flash */}
        <div style={{
          width: 2, height: 56, margin: '0 -6px', background: 'var(--amber)', borderRadius: 2,
          boxShadow: '0 0 18px 3px color-mix(in srgb, var(--amber) 70%, transparent)',
          opacity: 0, zIndex: 2,
        }} className={phase === 1 ? 'nxa-fc-splice' : undefined} />

        <div style={{
          padding: 4, borderRadius: '50%', border: '2px solid rgba(var(--amber-rgb),0.5)',
        }} className={phase === 1 ? 'nxa-fc-frame-r' : undefined}>
          <Avatar name={themName} avatarUrl={person.avatar_url} size={72} />
        </div>
      </div>

      <div style={{
        color: '#fff', fontWeight: 800, fontSize: 20, letterSpacing: '-0.01em', textAlign: 'center', padding: '0 30px',
        '--d': '0.6s',
      }} className={phase === 1 ? 'nxa-fc-fadeup' : undefined}>
        You’re following {themName}
      </div>

      {sharedCount > 0 && (
        <div style={{
          color: 'rgba(var(--ink-rgb),0.55)', fontSize: 13.5, marginTop: 9, textAlign: 'center',
          '--d': '0.72s',
        }} className={phase === 1 ? 'nxa-fc-fadeup' : undefined}>
          You both love <span style={{ color: 'var(--amber)', fontWeight: 700 }}>{sharedCount}</span> of the same show{sharedCount === 1 ? '' : 's'}
          {topGenre ? <> · fellow <span style={{ color: 'var(--amber)', fontWeight: 700 }}>{topGenre}</span> fans</> : null}
        </div>
      )}

      <div style={{
        position: 'absolute', bottom: 30, color: 'rgba(var(--ink-rgb),0.32)', fontSize: 11,
        '--d': '1.1s',
      }} className={phase === 1 ? 'nxa-fc-fadeup' : undefined}>
        Tap to continue
      </div>
    </div>
  )
}

const STYLES = `
@keyframes fcFrameL {
  from { transform: translateX(-70px) rotate(-8deg); opacity: 0; }
  to   { transform: translateX(0)     rotate(0);     opacity: 1; }
}
@keyframes fcFrameR {
  from { transform: translateX(70px)  rotate(8deg);  opacity: 0; }
  to   { transform: translateX(0)     rotate(0);     opacity: 1; }
}
@keyframes fcSplice {
  0%   { opacity: 0; transform: scaleY(0.2); }
  45%  { opacity: 1; transform: scaleY(1.15); }
  100% { opacity: 0.7; transform: scaleY(1); }
}
@keyframes fcFadeUp {
  from { transform: translateY(12px); opacity: 0; }
  to   { transform: translateY(0);    opacity: 1; }
}
`
